import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import { cn } from '@/utils'

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

type TooltipContentProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
  /**
   * Portal target for the tooltip content. Defaults to `document.body` (Radix default) when
   * omitted. Pass the root of a floating, portaled ancestor (e.g. `NodeEditToolbar`'s own
   * portal div) so the tooltip becomes a real DOM descendant of it — otherwise event guards
   * scoped to that ancestor (like a wheel listener blocking native browser zoom) never see
   * events landing on the tooltip, since Radix would portal it to `document.body` separately.
   */
  container?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Portal>['container']
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ children, className, sideOffset = 8, container, ...props }, ref) => (
  <TooltipPrimitive.Portal container={container}>
    <TooltipPrimitive.Content
      ref={ref}
      data-slot="tooltip-content"
      sideOffset={sideOffset}
      className={cn(
        'z-50 inline-flex max-w-sm items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-normal leading-4 text-primary-foreground shadow-md',
        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]',
        className
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow className="fill-primary" height={8} width={12} />
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
))

TooltipContent.displayName = TooltipPrimitive.Content.displayName

/** Kbd chip on primary tooltip — Figma Page-Editor-WASM (Larry) node 12792:823714. */
export const tooltipKbdChipClassName =
  'rounded-md border-0 bg-primary-foreground/20 px-1 font-sans text-xs font-medium leading-4 text-primary-foreground shadow-none'

const TooltipShortcut = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'inline-flex h-5 min-w-5 shrink-0 select-none items-center justify-center',
        tooltipKbdChipClassName,
        className
      )}
      {...props}
    />
  )
)

TooltipShortcut.displayName = 'TooltipShortcut'

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipShortcut }
